import React, { useEffect, useRef, useState } from 'react';
import { HashRouter } from 'react-router-dom';
import { useGamification } from '../hooks/GamificationContext';
import { XpToast } from '../components/XpToast';
import MomentHost from '../components/moments/MomentHost';
import { getXpToastMeta, useXpBonus, XP_EVENTS } from '../hooks/useXpBonus';
import { useToast } from '../hooks/useToast';
import { NavigationMenu } from '../components/NavigationMenu';
import { RadioProvider } from '../RadioContext';
import RadioPlayer from '../components/RadioPlayer';
import { useCrews } from '../hooks/useCrews';
import { useSingletonAudio } from '../hooks/useSingletonAudio';
import xpGainSound from '../assets/sounds/xp-gain.mp3';
import xpLoseSound from '../assets/sounds/xp-lose.mp3';
import { CHALLENGE_FOCUS_EVENT, isChallengeFocusEnabled, isXpSoundEnabled } from '../utils/settings';
import { usePfandEntries } from '../hooks/usePfandEntries';
import PublicProfile from '../components/PublicProfile';
import { crewsApiUrl, drinksApiUrl, nonce } from '../utils/runtime';
import { useAnnouncements } from '../hooks/useAnnouncements';
import ImportantAnnouncementModal from '../components/announcements/ImportantAnnouncementModal';
import AnnouncementBanner from '../components/announcements/AnnouncementBanner';
import ExpeditionLivePrompt from '../components/ExpeditionLivePrompt';
import AppRoutes from './routes';
import DiscoveryToast from '../components/collection/DiscoveryToast';
import '../style.css';
import '../styles/crew.css';
import '../styles/history.css';
import '../styles/map.css';
import '../styles/trashcan.css';
import '../styles/leaderboard.css';
import '../styles/avatar-picker.css';
import '../styles/profile-visits.css';
import '../styles/referrals.css';
import '../styles/expeditions.css';
import '../styles/moments.css';
import '../styles/captain-kork.css';
import '../styles/freibeuterhafen.css';
import '../styles/shipyard.css';
import '../styles/harbormaster.css';
import '../styles/piratenpost.css';
import '../styles/overview.css';
import '../styles/map-view-switch.css';
import '../styles/events.css';
import '../styles/help.css';
import '../styles/drink-collection.css';

function AppFooter() {
  return (
    <footer className="pfp-app-footer">
      <a
        href="https://pfandpirat.com/"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#7a4b17',
          textDecoration: 'none',
          marginRight: '16px',
          fontWeight: '600',
        }}
      >
        Zur Website
      </a>

      <a
        href="https://pfandpirat.com/impressum/"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#7a4b17',
          textDecoration: 'none',
          fontWeight: '600',
        }}
      >
        Impressum
      </a>
    </footer>
  );
}

function App() {
  const [rotation, setRotation] = useState(0);
  const { entries, add, update, remove, load, user, applyReceiptRedemptionResult } = usePfandEntries();
  const { xp, lastXpChange, clearLastXpChange, currentMoment, dismissCurrentMoment, applyGamificationPayload } = useGamification();

  const [showXpToast, setShowXpToast] = useState(false);
  const [xpToastChange, setXpToastChange] = useState(null);
  const [challengeFocusEnabled, setChallengeFocusEnabledState] = useState(() => {
    try {
      return isChallengeFocusEnabled();
    } catch {
      return false;
    }
  });

  const xpToastTimerRef = useRef(null);
  const handledXpToastIdRef = useRef(null);
  const activeToastRef = useRef(null);

  const [showScanner, setShowScanner] = useState(false);
  const [barcode, setBarcode] = useState('');
  const playXpGain = useSingletonAudio(xpGainSound, 0.2);
  const playXpLose = useSingletonAudio(xpLoseSound, 0.2);

  const { giveXP: giveXPBonus } = useXpBonus();
  const { Toasts } = useToast();
  const {
    centerAnnouncements,
    bannerAnnouncements,
    modalAnnouncement,
    unreadCount,
    loading: announcementsLoading,
    error: announcementsError,
    markRead: markAnnouncementRead,
    dismiss: dismissAnnouncement,
  } = useAnnouncements();

  const [xpUp, setXpUp] = useState(false);
  const [freeValue, setFreeValue] = useState('');
  const [filter, setFilter] = useState('today');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [cbCrew, setCbCrew] = useState(null);
  const [crewId, setCrewId] = useState(null);
  const [crewEntries, setCrewEntries] = useState([]);

  const { crews, crewMembers, create, join, leave, reload, loadMembers } = useCrews(crewsApiUrl, nonce);

  const params = new URLSearchParams(window.location.search);
  const profil = params.get('profil');
  const hash = window.location.hash.replace(/^#\/?/, '');

  if (profil && (!hash || hash === '')) {
    return <PublicProfile username={profil} />;
  }

  useEffect(() => {
    const refreshChallengeFocus = () => {
      try {
        setChallengeFocusEnabledState(isChallengeFocusEnabled());
      } catch {
        setChallengeFocusEnabledState(false);
      }
    };

    window.addEventListener(CHALLENGE_FOCUS_EVENT, refreshChallengeFocus);
    window.addEventListener('storage', refreshChallengeFocus);

    return () => {
      window.removeEventListener(CHALLENGE_FOCUS_EVENT, refreshChallengeFocus);
      window.removeEventListener('storage', refreshChallengeFocus);
    };
  }, []);

  useEffect(() => {
    if (!lastXpChange?.id) {
      return;
    }

    if (handledXpToastIdRef.current === lastXpChange.id) {
      return;
    }

    const currentToast = activeToastRef.current;
    const nextAwarded = Number(lastXpChange.awarded || 0);
    const nextEvent = String(lastXpChange.event || '');
    const nextPriority = nextEvent === XP_EVENTS.COMPLETE_DAILY ? 100 : Math.max(1, Math.abs(nextAwarded));

    if (
      currentToast &&
      currentToast.id !== lastXpChange.id &&
      Number(currentToast.priority || 0) > nextPriority
    ) {
      handledXpToastIdRef.current = lastXpChange.id;
      clearLastXpChange();
      return;
    }

    handledXpToastIdRef.current = lastXpChange.id;

    if (challengeFocusEnabled) {
      activeToastRef.current = null;
      setShowXpToast(false);
      setXpToastChange(null);

      if (xpToastTimerRef.current) {
        clearTimeout(xpToastTimerRef.current);
        xpToastTimerRef.current = null;
      }

      try {
        if (window.navigator?.vibrate) {
          window.navigator.vibrate(0);
        }
      } catch {}

      clearLastXpChange();
      return;
    }

    const toastMeta = getXpToastMeta(nextEvent, nextAwarded, lastXpChange.label);
    const nextToast = { ...lastXpChange, ...toastMeta, priority: nextPriority };
    activeToastRef.current = nextToast;

    setXpToastChange(nextToast);
    setShowXpToast(false);

    window.requestAnimationFrame(() => {
      setShowXpToast(true);
    });

    try {
      if (window.navigator?.vibrate) {
        window.navigator.vibrate(0);
      }
    } catch {}

    if (nextAwarded > 0) {
      if (isXpSoundEnabled()) {
        playXpGain();
      } else if (window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }
    } else if (nextAwarded < 0) {
      if (isXpSoundEnabled()) {
        playXpLose();
      } else if (window.navigator.vibrate) {
        window.navigator.vibrate([80, 60, 80]);
      }
    }


    if (xpToastTimerRef.current) {
      clearTimeout(xpToastTimerRef.current);
    }

    xpToastTimerRef.current = setTimeout(() => {
      setShowXpToast(false);
      setXpToastChange(null);
      activeToastRef.current = null;
      handledXpToastIdRef.current = null;

      try {
        if (window.navigator?.vibrate) {
          window.navigator.vibrate(0);
        }
      } catch {}

      clearLastXpChange();
      xpToastTimerRef.current = null;
    }, 2200);
  }, [challengeFocusEnabled, lastXpChange, playXpGain, playXpLose, xp, clearLastXpChange]);

  useEffect(() => {
    if (challengeFocusEnabled && currentMoment) {
      dismissCurrentMoment();
    }
  }, [challengeFocusEnabled, currentMoment, dismissCurrentMoment]);

  useEffect(() => {
    return () => {
      if (xpToastTimerRef.current) {
        clearTimeout(xpToastTimerRef.current);
      }

      try {
        if (window.navigator?.vibrate) {
          window.navigator.vibrate(0);
        }
      } catch {}
    };
  }, []);

  const currentCrewMembers = crewMembers?.[crewId] ?? [];
  const isCrewMember = currentCrewMembers.includes(user?.id);
  const momentBlockedByXpToast = Boolean(currentMoment && (lastXpChange || xpToastChange || showXpToast));
  const visibleMoment = challengeFocusEnabled || momentBlockedByXpToast ? null : currentMoment;

  const loadCrewEntries = (selectedCrewId) => {
    if (!selectedCrewId) return;
    fetch(`/wp-json/pfandpirat/v1/crews/${selectedCrewId}/entries`, {
      credentials: 'include',
      headers: { 'X-WP-Nonce': nonce },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setCrewEntries(data);
        else setCrewEntries([]);
      });
  };

  useEffect(() => {
    if (crewId) loadMembers(crewId);
  }, [crewId, loadMembers]);

  useEffect(() => {
    if (crewId && isCrewMember) loadCrewEntries(crewId);
  }, [crewId, isCrewMember, crewMembers]);

  useEffect(() => {
    if (!crewId && user?.id && Array.isArray(crews) && crews.length && crewMembers) {
      const myCrew = crews.find(
        (crew) =>
          (crewMembers[crew.id] || []).includes(Number(user.id)) || Number(crew.author) === Number(user.id)
      );
      if (myCrew) setCrewId(myCrew.id);
    }
  }, [crewId, crews, crewMembers, user]);

  const validEntries = isCrewMember && Array.isArray(crewEntries) ? crewEntries : [];
  const filtered = (crewId ? validEntries : entries).filter((entry) => {
    if (!entry.timestamp) return false;
    const [y, m, d, h, i, s] = entry.timestamp.split(/[- :]/);
    const entryDate = new Date(y, m - 1, d, h, i, s);
    const entryDateStr = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`;
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    switch (filter) {
      case 'today':
        return entryDateStr === todayStr;
      case 'yesterday': {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
        return entryDateStr === yesterdayStr;
      }
      case 'month': {
        const [year, month] = todayStr.split('-');
        const [entryYear, entryMonth] = entryDateStr.split('-');
        return year === entryYear && month === entryMonth;
      }
      case 'custom': {
        if (!customRange.from) return false;
        const fromDate = new Date(customRange.from);
        fromDate.setHours(0, 0, 0, 0);
        const toDate = customRange.to ? new Date(customRange.to) : new Date(customRange.from);
        toDate.setHours(23, 59, 59, 999);
        return new Date(entry.timestamp) >= fromDate && new Date(entry.timestamp) <= toDate;
      }
      case 'all':
      default:
        return true;
    }
  });

  const periodFiltered = (Array.isArray(entries) ? entries : []).filter((entry) => {
    if (!entry?.timestamp) return false;
    const entryDate = new Date(entry.timestamp);
    const now = new Date();
    switch (filter) {
      case 'today':
        return entryDate.toDateString() === now.toDateString();
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return entryDate.toDateString() === yesterday.toDateString();
      }
      case 'week': {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return entryDate >= weekAgo;
      }
      case 'month':
        return entryDate.getFullYear() === now.getFullYear() && entryDate.getMonth() === now.getMonth();
      default:
        return true;
    }
  });

  const displayEntries = user?.id
    ? periodFiltered.filter((entry) => Number(entry.user_id) === Number(user.id))
    : periodFiltered;

  const handleRemove = (id) => {
    remove(id).then((response) => {
      const awarded = Number(response?.awarded || 0);
      if (awarded !== 0 && typeof applyGamificationPayload === 'function') {
        applyGamificationPayload(response, {
          emitXpToast: true,
          event: response?.event || XP_EVENTS.REMOVE_ENTRY,
          awarded,
          toastLabel: response?.xp_toast?.label || 'Pfandeintrag gelöscht',
        });
      }
    });
  };

  return (
    <HashRouter>
      <RadioProvider>
        <div className="pfp-app-shell">
          <NavigationMenu unreadCount={unreadCount} />
          <DiscoveryToast />
          <div id="pfp-app-content" className="pfp-app-content">
          <AnnouncementBanner announcements={bannerAnnouncements} onDismiss={dismissAnnouncement} />
          <AppRoutes
            entries={entries}
            user={user}
            rotation={rotation}
            add={add}
            giveXPBonus={giveXPBonus}
            setXpUp={setXpUp}
            freeValue={freeValue}
            setFreeValue={setFreeValue}
            displayEntries={displayEntries}
            update={update}
            handleRemove={handleRemove}
            userColors={{}}
            filter={filter}
            setFilter={setFilter}
            customRange={customRange}
            setCustomRange={setCustomRange}
            load={load}
            applyReceiptRedemptionResult={applyReceiptRedemptionResult}
            crews={crews}
            crewMembers={crewMembers}
            crewId={crewId}
            setCrewId={setCrewId}
            create={create}
            join={join}
            leave={leave}
            reload={reload}
            crewEntries={crewEntries}
            setCbCrew={setCbCrew}
            loadMembers={loadMembers}
            cbCrew={cbCrew}
            drinksApiUrl={drinksApiUrl}
            setShowScanner={setShowScanner}
            barcode={barcode}
            setBarcode={setBarcode}
            announcements={centerAnnouncements}
            unreadCount={unreadCount}
            announcementsLoading={announcementsLoading}
            announcementsError={announcementsError}
            markAnnouncementRead={markAnnouncementRead}
          />

          <AppFooter />
          <ImportantAnnouncementModal
            announcement={modalAnnouncement}
            onClose={dismissAnnouncement}
          />
          <XpToast key={xpToastChange?.id || 'xp-toast'} change={xpToastChange} visible={showXpToast} />
          <MomentHost moment={visibleMoment} onClose={dismissCurrentMoment} />
          {!challengeFocusEnabled && <ExpeditionLivePrompt />}
          <Toasts />
          <RadioPlayer />
          </div>
        </div>
      </RadioProvider>
    </HashRouter>
  );
}

export default App;
